import {
  createSlice,
  createAsyncThunk,
  createEntityAdapter,
} from "@reduxjs/toolkit";
import { uncoveryApi } from "../../services";

const entitiesAdapter = createEntityAdapter();
const initialState = entitiesAdapter.getInitialState({
  selectedEntity: {},
  entityId: null,
  addOneStatus: "idle",
  fetchAll: "idle",
  errorMessage: "",
  fetchOneEntityStatus: "idle",
  fetchOneEntityErrorMessage: "",
  updateRecurrenceStatus: "idle",
  updateRecurrenceErrorMessage: "",
  hasNextPage: false,
  page: 1,
  pageSize: 50,
  sortBy: "createdAt",
  orderBy: "desc",
  searchTerm: "",
  count: 0,
  scrollPosition: 0,
});

const options = {
  headers: {
    Accept: "application/json",
    "Content-Type": "application/json",
  },
};

export const addOneEntity = createAsyncThunk(
  "entities/addOne",
  async (body, { rejectWithValue }) => {
    try {
      const response = await uncoveryApi.post("/entities", body, options);
      if (response.status === 201) {
        return response.data.data;
      } else {
        return rejectWithValue(response.data);
      }
    } catch (err) {
      return rejectWithValue(err.response.data);
    }
  }
);

export const fetchAllEntities = createAsyncThunk(
  "entities/getAll",
  async (_, { rejectWithValue, getState }) => {
    try {
      const { page, pageSize, sortBy, orderBy, searchTerm } = getState().entity;
      const response = await uncoveryApi.get(
        `/entities?page=${page}&pageSize=${pageSize}&sortBy=${sortBy}&orderBy=${orderBy}&searchTerm=${searchTerm}`
      );
      if (response.status === 200) {
        return response.data;
      } else {
        return rejectWithValue(response.data);
      }
    } catch (e) {
      return rejectWithValue(e.response.data);
    }
  }
);

export const fetchOneEntity = createAsyncThunk(
  "entities/fetchOne",
  async ({ entityId }, { rejectWithValue, getState }) => {
    try {
      const response = await uncoveryApi.get(`/entities/${entityId}`);
      if (response.status === 200) {
        return response.data.data;
      } else {
        return rejectWithValue(response.data);
      }
    } catch (e) {
      return rejectWithValue(e.response.data);
    }
  }
);

export const updateRecurrence = createAsyncThunk(
  "entities/updateRecurrence",
  async ({ entityId, isRecurrence }, { rejectWithValue }) => {
    const query = `/entities/${entityId}?isRecurrence=${isRecurrence}`;
    try {
      const response = await uncoveryApi.put(query);
      if (response.status === 200) {
        return response.data.data;
      } else {
        return rejectWithValue(response.data);
      }
    } catch (e) {
      return rejectWithValue(e.response.data);
    }
  }
);

export const entitySlice = createSlice({
  name: "entity",
  initialState,
  reducers: {
    refreshAddEntityStatus: (state) => {
      state.addOneStatus = "idle";
      return state;
    },
    refreshFetchEntityStatus: (state) => {
      state.fetchAll = "idle";
      return state;
    },
    refreshFetchOneEntityStatus: (state) => {
      state.fetchOneEntityStatus = "idle";
      return state;
    },
    refreshUpdateRecurrenceStatus: (state) => {
      state.updateRecurrenceStatus = "idle";
      return state;
    },
    updateEntityId: (state, { payload }) => {
      const { entityId } = payload;
      state.entityId = entityId;
      return state;
    },
    updateEntityUpdatedAt: (state, { payload }) => {
      const { updatedAt } = payload;
      if (state.selectedEntity.updatedAt) {
        state.selectedEntity.updatedAt = updatedAt;
      }
      return state;
    },
    updatePage: (state, { payload }) => {
      const { page } = payload;
      state.page = page;
      return state;
    },
    resetSearchTerm: (state) => {
      state.searchTerm = "";
      return state;
    },
    updateSearchTerm: (state, { payload }) => {
      const { searchTerm } = payload;
      state.page = 1;
      state.searchTerm = searchTerm;
      return state;
    },
    setSelectedEntity: (state, { payload }) => {
      const { entity } = payload;
      state.selectedEntity = entity;
      return state;
    },
    resetSelectedEntity: (state) => {
      state.selectedEntity = null;
      state.fetchOneEntityStatus = "idle";
      state.fetchOneEntityErrorMessage = "";
      return state;
    },
    resetEntityState: () => initialState,
  },
  extraReducers: {
    [fetchAllEntities.pending]: (state, { payload }) => {
      state.fetchAll = "loading";
    },
    [fetchAllEntities.fulfilled]: (state, { payload }) => {
      if (state.page === 1) {
        state.selectedEntity = payload.data[0];
        entitiesAdapter.setAll(state, payload.data);
      } else {
        entitiesAdapter.addMany(state, payload.data);
      }
      state.hasNextPage = payload.pageInfo.hasNextPage;
      state.fetchAll = "success";
    },
    [fetchAllEntities.rejected]: (state, { payload }) => {
      state.errorMessage = payload.message;
      state.fetchAll = "error";
    },
    [fetchOneEntity.pending]: (state, { payload }) => {
      state.fetchOneEntityStatus = "loading";
    },
    [fetchOneEntity.fulfilled]: (state, { payload }) => {
      state.selectedEntity = payload;
      state.entityId = payload.id;
      state.fetchOneEntityStatus = "success";
    },
    [fetchOneEntity.rejected]: (state, { payload }) => {
      state.fetchOneEntityErrorMessage = payload.message;
      state.fetchOneEntityStatus = "error";
    },
    [addOneEntity.pending]: (state, { payload }) => {
      state.addOneStatus = "loading";
    },
    [addOneEntity.fulfilled]: (state, { payload }) => {
      const createdEntity = payload;
      state.addOneStatus = "success";
      state.entityId = createdEntity.id;
    },
    [addOneEntity.rejected]: (state, { payload }) => {
      state.errorMessage = payload.message;
      state.addOneStatus = "error";
    },
    [updateRecurrence.pending]: (state, { payload }) => {
      state.updateRecurrenceStatus = "loading";
    },
    [updateRecurrence.fulfilled]: (state, { payload }) => {
      state.updateRecurrenceStatus = "success";
      state.selectedEntity.isRecurrence = payload[0].isRecurrence;
    },
    [updateRecurrence.rejected]: (state, { payload }) => {
      state.updateRecurrenceErrorMessage = payload.message;
      state.updateRecurrenceStatus = "error";
    },
  },
});

export const {
  refreshAddEntityStatus,
  refreshFetchEntityStatus,
  refreshFetchOneEntityStatus,
  refreshUpdateRecurrenceStatus,
  resetSelectedEntity,
  updateEntityId,
  updateEntityUpdatedAt,
  updatePage,
  updateSearchTerm,
  resetSearchTerm,
  resetEntityState,
  setSelectedEntity,
} = entitySlice.actions;

export default entitySlice.reducer;

export const {
  selectAll: selectEntities,
  selectById: selectEntityById,
  selectIds: selectEntityIds,
} = entitiesAdapter.getSelectors((state) => state.entity);

// export const selectEntityIds = createSelector(selectEntities, (entities) =>
//   entities.map((entity) => entity.id)
// );
